import { TaskPeriodBands } from "./TaskPeriodBands";
import { useMemo, useRef, type CSSProperties, type DragEvent } from "react";
import { format, isSameMonth, isToday } from "date-fns";
import { ru } from "date-fns/locale";
import { Check } from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import type { Identifier } from "ra-core";
import type { Task } from "../types";
import { installationOnDay, installationRangeLabel } from "./installation";
import {
  monthWeeks,
  taskDaysMap,
  taskCompleted,
  installationTracks,
} from "./calendarModel";

export function CalendarMonth({
  date,
  tasks,
  installations,
  colors,
  onDay,
  onTask,
  onStep,
  canMove,
  onDrop,
  personalIds,
  allowSwipe = true,
}: {
  date: Date;
  tasks: Task[];
  installations: Task[];
  colors: Map<string, string>;
  onDay: (date: Date) => void;
  onTask: (id: Identifier) => void;
  onStep: (step: number) => void;
  canMove: (task: Task) => boolean;
  onDrop: (event: DragEvent, day: Date) => void;
  personalIds: Set<string>;
  allowSwipe?: boolean;
}) {
  const weeks = monthWeeks(date);
  const firstDay = format(weeks[0][0], "yyyy-MM-dd"),
    lastDay = format(weeks.at(-1)![6], "yyyy-MM-dd");
  const daily = useMemo(
    () =>
      taskDaysMap(
        tasks,
        new Date(`${firstDay}T00:00:00`),
        new Date(`${lastDay}T00:00:00`),
      ),
    [tasks, firstDay, lastDay],
  );
  const dayTasksFor = (day: Date) => daily.get(format(day, "yyyy-MM-dd")) || [];
  const tracks = installationTracks(installations);
  const periods = installationTracks(
    tasks
      .filter((t) => t.scheduled_start && t.scheduled_end)
      .map((task) => ({
        ...task,
        installation_start: format(
          new Date(task.scheduled_start!),
          "yyyy-MM-dd",
        ),
        installation_end: format(
          new Date(new Date(task.scheduled_end!).getTime() - 1),
          "yyyy-MM-dd",
        ),
      })),
  );
  const gesture = useRef({ x: 0, y: 0, swiped: false });
  const openDay = (day: Date) => {
    if (!gesture.current.swiped) onDay(day);
  };
  return (
    <div
      className="crm-month flex min-h-0 flex-1 flex-col"
      aria-label={format(date, "LLLL yyyy", { locale: ru })}
    >
      <div className="crm-weekdays" aria-hidden="true">
        {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div
        role="grid"
        aria-label="Дни месяца"
        className="crm-month-grid"
        onPointerDown={(e) => {
          gesture.current = { x: e.clientX, y: e.clientY, swiped: false };
        }}
        onKeyDownCapture={() => {
          gesture.current.swiped = false;
        }}
        onPointerUpCapture={(e) => {
          if (!allowSwipe || e.pointerType !== "touch") return;
          const dx = e.clientX - gesture.current.x,
            dy = e.clientY - gesture.current.y;
          if (Math.abs(dx) > 65 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            gesture.current.swiped = true;
            onStep(dx < 0 ? 1 : -1);
          }
        }}
      >
        {weeks.map((days, week) => {
          const first = format(days[0], "yyyy-MM-dd"),
            last = format(days[6], "yyyy-MM-dd");
          const bands = tracks.filter(
            ({ task }) =>
              task.installation_start! <= last &&
              task.installation_end! >= first,
          );
          const montageRows = [...new Set(bands.map((b) => b.track))].sort(
            (a, b) => a - b,
          );
          const lanes = montageRows.length;
          const periodBands = periods.filter(
            ({ task }) =>
              task.installation_start! <= last &&
              task.installation_end! >= first,
          );
          const taskRows = [...new Set(periodBands.map((b) => b.track))].sort(
            (a, b) => a - b,
          );
          const periodLanes = taskRows.length;
          const maxDots = Math.max(
            0,
            ...days.map((day) => dayTasksFor(day).length),
          );
          return (
            <div
              role="row"
              className="crm-month-week"
              key={week}
              onDragOver={(event) => {
                if (event.dataTransfer.types.includes("text/task-id"))
                  event.preventDefault();
              }}
              onDrop={(event) => {
                const box = event.currentTarget.getBoundingClientRect();
                const column = Math.min(
                  6,
                  Math.max(
                    0,
                    Math.floor(((event.clientX - box.left) / box.width) * 7),
                  ),
                );
                onDrop(event, days[column]);
              }}
              style={
                {
                  "--montage-tracks": lanes,
                  "--period-tracks": periodLanes,
                  "--dot-rows": Math.ceil(maxDots / 4),
                } as CSSProperties
              }
            >
              <div className="crm-week-cells">
                {days.map((day) => {
                  const key = format(day, "yyyy-MM-dd"),
                    dayTasks = dayTasksFor(day),
                    dayInstallations = installations.filter((t) =>
                      installationOnDay(t, key),
                    );
                  return (
                    <div
                      role="gridcell"
                      key={key}
                      className={`crm-day-cell ${!isSameMonth(day, date) ? "crm-outside-month" : ""}`}
                      data-calendar-date={format(day, "yyyy-MM-dd")}
                      onClick={() => openDay(day)}
                      onDragOver={(e) => {
                        if (e.dataTransfer.types.includes("text/task-id"))
                          e.preventDefault();
                      }}
                      onDrop={(e) => onDrop(e, day)}
                    >
                      <button
                        type="button"
                        className={`crm-date-number ${isToday(day) ? "crm-date-today" : ""}`}
                        aria-current={isToday(day) ? "date" : undefined}
                        aria-label={`${format(day, "d MMMM yyyy", { locale: ru })}: монтажей ${dayInstallations.length}, задач ${dayTasksFor(day).length}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDay(day);
                        }}
                      >
                        {format(day, "d")}
                      </button>
                      <div className="crm-cell-events">
                        {dayTasks.length > 0 && (
                          <button
                            type="button"
                            className="crm-task-dots"
                            aria-label={`${dayTasks.length} задач на ${format(day, "d MMMM", { locale: ru })}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openDay(day);
                            }}
                          >
                            {dayTasks.map((task) =>
                              taskCompleted(task) ? (
                                <Check
                                  key={task.id}
                                  className="crm-task-done-icon"
                                  aria-label="Выполнено"
                                />
                              ) : (
                                <span
                                  key={task.id}
                                  className={
                                    personalIds.has(String(task.id))
                                      ? "crm-dot-personal"
                                      : ""
                                  }
                                  style={{
                                    background:
                                      colors.get(String(task.deal_id)) ||
                                      "var(--primary)",
                                  }}
                                />
                              ),
                            )}
                            <small>{dayTasks.length}</small>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <TaskPeriodBands
                bands={periodBands.map((b) => ({
                  ...b,
                  track: taskRows.indexOf(b.track),
                }))}
                personalIds={personalIds}
                days={days}
                colors={colors}
                canMove={canMove}
                onOpen={(id) => {
                  if (!gesture.current.swiped) onTask(id);
                }}
              />
              <div className="crm-installation-layer">
                {bands.map(({ task, track }) => {
                  const begin = task.installation_start! >= first;
                  const finish = task.installation_end! <= last;
                  const col = Math.max(
                    0,
                    days.findIndex(
                      (d) =>
                        format(d, "yyyy-MM-dd") >= task.installation_start!,
                    ),
                  );
                  const endCol = finish
                    ? days.findIndex(
                        (d) =>
                          format(d, "yyyy-MM-dd") === task.installation_end!,
                      )
                    : 6;
                  return (
                    <Tooltip key={task.id}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="crm-installation-band"
                          data-start={begin}
                          data-end={finish}
                          aria-label={
                            task.text +
                            ": " +
                            installationRangeLabel(task) +
                            (taskCompleted(task) ? " · Выполнено" : "")
                          }
                          style={
                            {
                              gridColumn:
                                col + 1 + " / span " + (endCol - col + 1),
                              gridRow: montageRows.indexOf(track) + 1,
                              "--band-color": taskCompleted(task)
                                ? "#888888"
                                : colors.get(String(task.deal_id)) ||
                                  "var(--primary)",
                            } as CSSProperties
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!gesture.current.swiped) onTask(task.id);
                          }}
                        >
                          <span aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={4}
                        className="max-w-72"
                      >
                        <strong className="block">{task.text}</strong>
                        <span className="block mt-1">
                          {installationRangeLabel(task)} · включительно
                        </span>
                        {taskCompleted(task) && (
                          <span className="mt-1 flex items-center gap-1">
                            <Check className="size-3" /> Выполнено
                          </span>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
